<section class="mx-auto flex w-full max-w-4xl flex-col gap-4 rounded-lg bg-card p-6">
    <header class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-3">
            <i [class]="icon()" class="ph text-2xl" aria-hidden="true"></i>
            <h2 class="m-0 font-display text-xl font-semibold text-text">{{ headlineKey() | translate }}</h2>
        </div>
        <p-tag [value]="tag().labelKey | translate" [severity]="tag().severity" [rounded]="true" />
    </header>

    <p class="m-0 text-sm text-text-2">{{ bodyKey() | translate }}</p>

    @let currentState = state(); @if (currentState) {
    <p class="m-0 text-sm text-text-muted">
        {{ 'FUNPAY_STATUS_LAST_STEP' | translate }}: {{ stepKey() | translate }}
        <span class="mx-1">·</span>
        {{ currentState.updatedAt | date: 'short' }}
    </p>

    @if (currentState.status === 'FAILED' && currentState.error?.message) {
    <p class="m-0 rounded-md bg-red-500/10 p-3 font-mono text-xs text-red-500">
        {{ "SOME_OF_THE_PROVIDED_INFORMATION_INCORRECT" | translate}}
    </p>
    } }

    <!-- Adyen's verdict, as delivered by the platform webhook -->
    @let adyenVerification = verification(); @if (adyenVerification) {
    <dl class="grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-2">
        <div class="flex flex-col">
            <dt class="text-sm text-text-muted">{{ 'FUNPAY_VERIFICATION_PAYMENTS' | translate }}</dt>
            <dd
                class="m-0 flex items-center gap-2 font-medium"
                [class]="adyenVerification.canReceivePayments ? 'text-paid' : 'text-text-2'">
                <i [class]="adyenVerification.canReceivePayments ? 'ph-fill ph-check-circle' : 'ph ph-clock'" aria-hidden="true"></i>
                {{ (adyenVerification.canReceivePayments ? 'FUNPAY_VERIFICATION_ALLOWED' : 'FUNPAY_VERIFICATION_PENDING') | translate }}
            </dd>
        </div>
        <div class="flex flex-col">
            <dt class="text-sm text-text-muted">{{ 'FUNPAY_VERIFICATION_PAYOUTS' | translate }}</dt>
            <dd class="m-0 flex items-center gap-2 font-medium" [class]="adyenVerification.canPayOut ? 'text-paid' : 'text-text-2'">
                <i [class]="adyenVerification.canPayOut ? 'ph ph-fill ph-check-circle' : 'ph ph-clock'" aria-hidden="true"></i>
                {{ (adyenVerification.canPayOut ? 'FUNPAY_VERIFICATION_ALLOWED' : 'FUNPAY_VERIFICATION_PENDING') | translate }}
            </dd>
        </div>
    </dl>

    @if (adyenVerification.problems?.length) {
    <div class="flex flex-col gap-2 rounded-md bg-amber-500/10 p-3">
        <p class="m-0 flex items-center gap-2 text-sm font-medium text-amber-600">
            <i class="ph ph-warning-circle"></i>
            {{ 'FUNPAY_VERIFICATION_PROBLEMS' | translate }}
        </p>
        <ul class="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-text-2">
            @for (problem of adyenVerification.problems; track problem) {
            <li>{{ problem }}</li>
            }
        </ul>
    </div>
    } } @let referenceList = references(); @if (referenceList.length) {
    <dl class="grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-2">
        @for (reference of referenceList; track reference.labelKey) {
        <div class="flex flex-col">
            <dt class="text-sm text-text-muted">{{ reference.labelKey | translate }}</dt>
            <dd class="m-0 font-mono text-sm text-text">{{ reference.value }}</dd>
        </div>
        }
    </dl>
    } @if (actionError()) {
    <p class="m-0 flex items-center gap-2 rounded-md bg-red-500/10 p-3 text-sm text-red-500">
        <i class="ph ph-warning-circle"></i>
        {{ actionError() | translate }}
    </p>
    }

    <footer class="flex flex-wrap justify-end gap-3 border-t border-border pt-4">
        @if (canResync() && ids()?.accountHolderId) {
        <button
            pButton
            type="button"
            severity="secondary"
            [text]="true"
            [loading]="busy()"
            [disabled]="busy()"
            class="mr-auto"
            (click)="resync.emit()">
            <i class="ph ph-arrows-clockwise mr-1.5"></i>
            {{ 'FUNPAY_RESYNC_ACCOUNT_HOLDER' | translate }}
        </button>
        }
        @if (canEditDetails()) {
        <button pButton type="button" [text]="true" [disabled]="busy()" (click)="editDetails.emit()">
            <i class="ph ph-pencil-simple mr-1.5"></i>
            {{ 'FUNPAY_EDIT_DETAILS' | translate }}
        </button>
        } @if (tone() === 'failed') {
        <button pButton type="button" [loading]="busy()" [disabled]="busy()" (click)="retry.emit()">
            <i class="ph ph-arrow-clockwise mr-1.5"></i>
            {{ 'FUNPAY_TRY_AGAIN' | translate }}
        </button>
        } @else if (tone() === 'progress') {
        <button pButton type="button" [loading]="busy()" [disabled]="busy()" (click)="retry.emit()">
            <i class="ph ph-play mr-1.5"></i>
            {{ 'FUNPAY_RESUME_APPLICATION' | translate }}
        </button>
        } @if (canContinue()) {
        <button
            pButton
            type="button"
            [outlined]="tone() !== 'submitted'"
            [loading]="busy()"
            [disabled]="busy()"
            (click)="continueOnboarding.emit()">
            <i class="ph ph-arrow-square-out mr-1.5"></i>
            {{ 'FUNPAY_CONTINUE_ONBOARDING' | translate }}
        </button>
        }
    </footer>
</section>
